/**
 * OIDC Dynamic Client Registration (RFC 7591) admin queries.
 *
 * Routes:
 *   GET    /api/v1/tenants/{id}/oauth/initial-access-tokens
 *   POST   /api/v1/tenants/{id}/oauth/initial-access-tokens
 *   DELETE /api/v1/tenants/{id}/oauth/initial-access-tokens/{iat_id}
 *   GET    /api/v1/tenants/{id}/oauth/dynamic-registration/policy
 *   PUT    /api/v1/tenants/{id}/oauth/dynamic-registration/policy
 *
 * Mutations route through withStepUp because IAT mint + policy
 * changes are gates on the same RBAC permission (applications.manage)
 * as a manual application create — the server may demand a fresh
 * passkey assertion to authorise the call. The list query is a
 * plain GET; no step-up required to read.
 *
 * The IAT plaintext returned from the mint endpoint is NEVER
 * setQueryData'd back into a cache; UIs consume it from the
 * mutation's resolved promise value, render it once, and
 * discard. The list invalidation refetches the summary-only
 * shape so the table reflects the new row without ever round-
 * tripping the plaintext through React Query's memo store.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  dcrPolicySchema,
  iatListResponseSchema,
  mintIATResponseSchema,
  registrationReviewListSchema,
  registrationReviewSchema,
  type DCRPolicy,
  type DCRPolicyPatch,
  type IATListResponse,
  type MintIATInput,
  type MintIATResponse,
  type RegistrationReview,
} from "./schemas";

const base = (tenantID: string) =>
  `/api/v1/tenants/${encodeURIComponent(tenantID)}/oauth`;

// ── IAT list ────────────────────────────────────────────────

export async function fetchIATs(
  tenantID: string,
  includeRevoked: boolean,
): Promise<IATListResponse> {
  const qs = includeRevoked ? "?include_revoked=true" : "";
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/initial-access-tokens${qs}`,
  );
  return iatListResponseSchema.parse(raw);
}

export function useIATsQuery(tenantID: string, includeRevoked: boolean) {
  return useQuery<IATListResponse, ApiError>({
    queryKey: queryKeys.oauthDynreg.iats(tenantID, includeRevoked),
    queryFn: () => fetchIATs(tenantID, includeRevoked),
  });
}

// ── Mint IAT ────────────────────────────────────────────────

// toMintWireBody maps the form's days-based + maybe-empty
// fields to the JSON body the backend expects. The conversion
// is small and lives here (not in the schema's transform) so
// the form's Zod parse keeps a clean shape and the wire layer
// can evolve independently.
function toMintWireBody(input: MintIATInput): Record<string, unknown> {
  const body: Record<string, unknown> = { label: input.label.trim() };
  if (input.maxRegistrations !== undefined && input.maxRegistrations > 0) {
    body.max_registrations = input.maxRegistrations;
  }
  if (input.expiresInDays !== undefined && input.expiresInDays > 0) {
    const expiresAt = new Date(
      Date.now() + input.expiresInDays * 86_400 * 1000,
    ).toISOString();
    body.expires_at = expiresAt;
  }
  return body;
}

export function useMintIATMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<MintIATResponse, ApiError, MintIATInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/initial-access-tokens`, {
          method: "POST",
          body: toMintWireBody(input),
        }).then((raw) => mintIATResponseSchema.parse(raw)),
      ),
    onSuccess: () => {
      // Refetch the list so the new row appears. NOTE: we
      // deliberately do NOT setQueryData with the response —
      // that would put the plaintext token in the React Query
      // cache, where memo'd selectors + devtools could surface
      // it. The cache only ever sees summary-only rows.
      qc.invalidateQueries({ queryKey: queryKeys.oauthDynreg.all(tenantID) });
    },
  });
}

// ── Revoke IAT ───────────────────────────────────────────────

export function useRevokeIATMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (iatID) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/initial-access-tokens/${encodeURIComponent(iatID)}`,
          { method: "DELETE" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.oauthDynreg.all(tenantID) });
    },
  });
}

// ── Policy ──────────────────────────────────────────────────

export async function fetchPolicy(tenantID: string): Promise<DCRPolicy> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/dynamic-registration/policy`,
  );
  return dcrPolicySchema.parse(raw);
}

export function usePolicyQuery(tenantID: string) {
  return useQuery<DCRPolicy, ApiError>({
    queryKey: queryKeys.oauthDynreg.policy(tenantID),
    queryFn: () => fetchPolicy(tenantID),
  });
}

export function useUpdatePolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<DCRPolicy, ApiError, DCRPolicyPatch>({
    mutationFn: (patch) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/dynamic-registration/policy`, {
          method: "PUT",
          body: patch,
        }).then((raw) => dcrPolicySchema.parse(raw)),
      ),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.oauthDynreg.policy(tenantID), data);
      qc.invalidateQueries({ queryKey: queryKeys.oauthDynreg.all(tenantID) });
    },
  });
}

export function useRegistrationReviewsQuery(tenantID: string) {
  return useQuery({
    queryKey: queryKeys.oauthDynreg.reviews(tenantID, "pending"),
    queryFn: () => fetchRegistrationReviews(tenantID),
  });
}

export async function fetchRegistrationReviews(tenantID: string) {
  return registrationReviewListSchema.parse(await apiFetch<unknown>(`${base(tenantID)}/dynamic-registration/reviews?state=pending`));
}

export function useReviewRegistrationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<RegistrationReview, ApiError, { review: RegistrationReview; decision: "approve" | "reject"; ownerAdminID?: string; reason?: string }>({
    mutationFn: ({ review, decision, ownerAdminID, reason }) => withStepUp(async () => registrationReviewSchema.parse(await apiFetch<unknown>(
      `${base(tenantID)}/dynamic-registration/reviews/${encodeURIComponent(review.id)}/${decision}`,
      { method: "POST", body: { expected_version: review.version, owner_admin_id: ownerAdminID, reason } },
    ))),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.oauthDynreg.all(tenantID) }); },
  });
}
